import { USER_CREATE_BUYER } from "@/gql/user-mutations";
import { logger } from "@/utils/logger";
import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import { toast } from "sonner";

export interface User {
	groups?: Array<{
		id: string;
		relationship: string;
	}>;
}

/**
 * Get the buyer group ID from the user's groups
 */
export const getBuyerGroupId = (user: User | null | undefined): string | undefined => {
	return user?.groups?.find(group => group.relationship === "buyer")?.id;
};

/**
 * Show error toast with consistent styling
 */
export const showErrorToast = (message: string, description?: string) => {
	toast.error(message, {
		description: description || "Please try again later",
		duration: 5000,
		position: "bottom-right",
	});
};

/**
 * Hook to create a buyer group for the current user
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useCreateBuyerGroup = (refetchUser?: (user: any) => Promise<void>) => {
	const [userUpdateMutation] = useMutation(USER_CREATE_BUYER);

	const createBuyerGroup = useCallback(async (): Promise<string | undefined> => {
		try {
			const { data: userRes, errors: userErr } = await userUpdateMutation({
				variables: {
					params: {
						update: { relationship: "buyer" },
					},
				},
			});
			const groups = userRes?.user?.update?.groups;
			if (userErr || !groups?.length) {
				logger.error(userErr);
				showErrorToast("Could not create buyer group");
				return;
			}
			if (userRes?.user?.update && refetchUser) await refetchUser(userRes?.user?.update);
			return groups.find((group: { id: string; relationship: string }) => group.relationship === "buyer")?.id;
		} catch (e) {
			logger.error(e);
			showErrorToast("Could not create buyer group");
			return;
		}
	}, [userUpdateMutation, refetchUser]);

	return createBuyerGroup;
};

/**
 * Hook that provides common buyer helper functions
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useBuyerHelpers = (user: User | null | undefined, refetchUser?: (user: any) => Promise<void>) => {
	const createBuyerGroup = useCreateBuyerGroup(refetchUser);

	const getBuyerGroupIdCallback = useCallback(() => getBuyerGroupId(user), [user]);

	return {
		getBuyerGroupId: getBuyerGroupIdCallback,
		createBuyerGroup,
		showErrorToast,
	};
};
